import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { SelectPesquisavelComponent } from '@shared/components/select-pesquisavel/select-pesquisavel.component';
import { ModeloDocumentoEnum } from '@shared/enums/dfe/modelo-documento.enum';
import { TipoAmbienteEnum } from '@shared/enums/dfe/tipo-ambiente.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { EmissorService } from '@shared/services/apis/dfe/emissor.service';
import { NaturezaOperacaoService } from '@shared/services/apis/motor-fiscal/natureza-operacao.service';
import { EmissorElegivelStruct } from '@shared/services/structs/dfe/emissor-elegivel.struct';
import { NaturezaOperacaoResumoStruct } from '@shared/services/structs/motor-fiscal/natureza-operacao-resumo.struct';

export class ReatribuirNaturezasEmissorDialogData {
    idEmissor: number;
    nomeEmissor: string;
    /** Ação que depende da reatribuição, no mesmo texto da mensagem de bloqueio: "excluir este emissor". */
    descricaoAcao: string;
    naturezas: NaturezaOperacaoResumoStruct[];
}

/** Opção do select: o elegível com o texto "{nome} · Série {serie} · {ambiente}". */
type EmissorElegivelOpcao = EmissorElegivelStruct & { descricaoExibicao: string };

/**
 * Reatribui as naturezas de operação que usam um emissor antes de excluí-lo, inativá-lo ou
 * desativar a NF-e dele. Fecha com `true` somente depois que a reatribuição foi gravada; quem
 * abriu o dialog executa a ação no emissor em seguida.
 */
@Component({
    selector: 'app-reatribuir-naturezas-emissor-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatProgressSpinnerModule,
        NgxMaskPipe,
        AlertCardComponent,
        SelectPesquisavelComponent,
    ],
    providers: [provideNgxMask()],
    templateUrl: './reatribuir-naturezas-emissor-dialog.component.html',
    styleUrl: './reatribuir-naturezas-emissor-dialog.component.css',
})
export class ReatribuirNaturezasEmissorDialogComponent implements OnInit {
    public readonly cfopMaskPatterns = { '0': { pattern: /[0-9?]/ } };
    public readonly idEmissorDestinoControl = new FormControl<number | null>(null);
    public emissoresElegiveis: EmissorElegivelOpcao[] = [];
    public isCarregandoEmissores = true;
    public isSalvando = false;

    private readonly descricaoAmbiente: Record<number, string> = {
        [TipoAmbienteEnum.Producao]: 'Produção',
        [TipoAmbienteEnum.Homologacao]: 'Homologação',
    };

    constructor(
        private dialogRef: MatDialogRef<ReatribuirNaturezasEmissorDialogComponent, boolean>,
        private emissorService: EmissorService,
        private naturezaOperacaoService: NaturezaOperacaoService,
        private alertService: AlertService,
        @Inject(MAT_DIALOG_DATA) public data: ReatribuirNaturezasEmissorDialogData,
    ) {}

    ngOnInit(): void {
        this.emissorService.getElegiveis(ModeloDocumentoEnum.NFe).subscribe({
            next: response => {
                this.isCarregandoEmissores = false;
                if (response.isError) {
                    this.alertService.show('Erro', response.errorDescription, AlertType.error);
                    return;
                }
                // O próprio emissor que está sendo alterado não pode receber as naturezas.
                this.emissoresElegiveis = (response.listaEmissorElegivel ?? [])
                    .filter(emissor => emissor.idEmissor !== this.data.idEmissor)
                    .map(emissor => ({ ...emissor, descricaoExibicao: this.descreverEmissor(emissor) }));
            },
            error: () => {
                this.isCarregandoEmissores = false;
                this.alertService.show('Erro inesperado', 'Não foi possível carregar os emissores.', AlertType.error);
            },
        });
    }

    public get isNenhumSelecionado(): boolean {
        return this.idEmissorDestinoControl.value == null;
    }

    public get hintEmissorDestino(): string {
        return !this.isCarregandoEmissores && this.emissoresElegiveis.length === 0
            ? 'Não há outro emissor ativo com NF-e ativa. As naturezas passarão a usar o emissor padrão.'
            : '';
    }

    public confirmar(): void {
        if (this.isSalvando) return;

        this.isSalvando = true;
        this.naturezaOperacaoService.reatribuirEmissor({
            idsNaturezaOperacao: this.data.naturezas.map(natureza => natureza.idNaturezaOperacao),
            idEmissor: this.idEmissorDestinoControl.value ?? null,
        }).subscribe({
            next: response => {
                this.isSalvando = false;
                if (response.isError) {
                    this.alertService.show('Erro', response.errorDescription, AlertType.error);
                    return;
                }
                this.dialogRef.close(true);
            },
            error: () => {
                this.isSalvando = false;
                this.alertService.show('Erro inesperado', 'Não foi possível reatribuir as naturezas de operação.', AlertType.error);
            },
        });
    }

    public cancelar(): void {
        this.dialogRef.close(false);
    }

    private descreverEmissor(emissor: EmissorElegivelStruct): string {
        const partes = [emissor.nome];
        if (emissor.serie != null) partes.push(`Série ${emissor.serie}`);
        partes.push(this.descricaoAmbiente[emissor.idTipoAmbiente] ?? '');
        return partes.filter(Boolean).join(' · ');
    }
}
